import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { FileUpload, FileUploadErrorEvent, FileUploadEvent, FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import { OverlayPanel } from 'primeng/overlaypanel';
import { AppConfig } from 'src/app/app-config.loader';
import { GassClaims } from 'src/app/auth/gass-claims';
import { AllegatiService } from 'src/app/core/allegati.service';
import { AllegatiCondivisiDetails, DocumentoAllegato, Intervallo, RiproduzioneJson, TipoAllegato } from 'src/app/shared/models/documentoAllegato.model';

@Component({
  selector: 'app-allegati',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './allegati.component.html',
  styleUrls: ['./allegati.component.scss']
})
export class AllegatiComponent {
  documentId = input.required<number>();
  allegati = input<DocumentoAllegato[]>([]);
  allegatiCondivisi = input<AllegatiCondivisiDetails[]>([]);
  updatingAllegati = input<boolean>(false);
  isLocked = input.required<boolean>();
  hasRiferimenti = input<boolean>(false);
  isDeparture = input<boolean>(false);
  showHeader = input<boolean>(true);
  showPrimaryCheckbox = input<boolean>(true);
  tipoAllegato = input<TipoAllegato>(TipoAllegato.Allegato);
  showSharedFiles = input<boolean>(true);
  beforeUpload = output();
  uploadCompleted = output();
  uploadError = output<string>();
  scan = output();
  scanCancelled = output();
  downloadFile = output<DocumentoAllegato>();
  deleteFile = output<DocumentoAllegato>();
  showPassword = output<DocumentoAllegato>();
  setPrimaryAllegato = output<DocumentoAllegato>();
  changeRiproduzione = output<DocumentoAllegato>();
  relaunchOCR = output<DocumentoAllegato>();
  modifyOCR = output<DocumentoAllegato>();
  riproduciAllegati = output();
  showAllegatiCondivisi = output();
  allegatoToPreview = output<string>();
  allegatoToUpload = output<DocumentoAllegato>()
  mostraCondivisi = signal<boolean>(false);
  showAddAllegatiDialog = signal<boolean>(false);
  uploadDescription = signal<string>('');
  uploadDescriptionTouched = signal<boolean>(false);
  uploadDescriptionValue = computed(() => this.uploadDescription().trim());
  isUploadDescriptionInvalid = computed(() => this.uploadDescriptionTouched() && this.uploadDescriptionValue().length === 0);
  pendingFiles = signal<File[]>([]);
  pendingUploadCount = signal<number>(0);
  uploadingInProgress = signal<boolean>(false);
  scannedFile = input<File | null>(null);
  scanInProgress = input<boolean>(false);
  pendingScanRequestId = input<string | null>(null);
  filesReadyForUpload = computed(() => {
    const pendingFiles = this.pendingFiles();
    if (pendingFiles.length > 0) {
      return pendingFiles;
    }

    const scannedFile = this.scannedFile();
    return scannedFile ? [scannedFile] : [];
  });
  isConfirmUploadDisabled = computed(() =>
    this.filesReadyForUpload().length === 0
    || this.uploadDescriptionValue().length === 0
    || this.uploadingInProgress()
    || this.isLocked()
  );


  fileupload = viewChild<FileUpload>('fileUpload');
  // @ViewChild('opallegato') panel: OverlayPanel;

  public readonly TipoAllegato = TipoAllegato;
  public uploadUrl = computed(() => `${this.appConfig.settings.apiRoot}/allegati/upload/${this.documentId()}`);
  public addButtonLabel = computed(() => this.tipoAllegato() === TipoAllegato.Annesso ? 'Aggiungi annessi' : 'Aggiungi allegati');
  public addDialogTitle = computed(() => this.tipoAllegato() === TipoAllegato.Annesso ? 'Aggiungi annessi' : 'Aggiungi allegati');
  public emptyMessage = computed(() => this.tipoAllegato() === TipoAllegato.Annesso ? 'Nessun annesso presente' : 'Nessun allegato presente');
  public descriptionText = computed(() => this.tipoAllegato() === TipoAllegato.Annesso
    ? 'Carica nuovi file per aggiungerli tra gli annessi del documento.'
    : 'Carica nuovi file oppure avvia la scansione per aggiungerli al documento.');
  public showScanButton = computed(() => this.tipoAllegato() === TipoAllegato.Allegato);
  allegatiToShow = computed(() => {
    if (this.showSharedFiles() && this.mostraCondivisi()) {
      return this.allegati()
    } else {
      return this.allegati().filter(x => !x.fromSharedFolder);
    }
  });
  public GassClaims = GassClaims;
  public selectedAllegato: DocumentoAllegato | null = null;
  public reproPages: string[] | null = null;
  public riproduciTutto = false;
  public reproErrorMessage: string | null = null;
  public testoOCROriginale: string | undefined;

  private readonly allegatiService = inject(AllegatiService);
  private readonly appConfig = inject(AppConfig);
  private lastQueuedScannedFile: File | null = null;

  constructor() {
    effect(() => {
      const scannedFile = this.scannedFile();
      const uploader = this.fileupload();
      if (!scannedFile) {
        this.lastQueuedScannedFile = null;
        return;
      }

      if (!uploader || scannedFile === this.lastQueuedScannedFile) {
        return;
      }

      this.queueScannedFile(scannedFile, uploader);
    });
  }

  public deleteClick(e: MouseEvent, allegato: DocumentoAllegato): void {
    e.stopPropagation();
    this.deleteFile.emit(allegato);
  }

  public relaunchOCRClick(e: MouseEvent, allegato: DocumentoAllegato): void {
    e.stopPropagation();
    if (allegato.fileName.toLowerCase().endsWith('.pdf') || allegato.fileName.toLowerCase().endsWith('.tiff')) {
      this.relaunchOCR.emit(allegato);
    }
  }

  public relaunchOCRandCloseClick(e: MouseEvent, allegato: DocumentoAllegato, overlaypanel: OverlayPanel): void {
    overlaypanel.hide();
    this.relaunchOCRClick(e, allegato);
  }

  public showPasswordClick(e: MouseEvent, allegato: DocumentoAllegato): void {
    e.stopPropagation();
    this.showPassword.emit(allegato);
  }

  public primaryChange(checked: boolean, allegato: DocumentoAllegato): void {
    if (!this.showPrimaryCheckbox() || !checked || allegato.isPrimario || this.isLocked() || allegato.fromSharedFolder) {
      return;
    }

    this.setPrimaryAllegato.emit(allegato);
  }

  public showToggle(event: Event, allegato: DocumentoAllegato, overlaypanel: OverlayPanel): void {
    this.selectedAllegato = allegato;
    this.riproduciTutto = this.getRiproduciTuttoFromDocumento(allegato.riproduzione);
    this.reproPages = this.reproPagesFromDocumento(allegato.riproduzione);
    overlaypanel.toggle(event);
  }

  public showToggleOCR(event: Event, allegato: DocumentoAllegato, overlaypanel: OverlayPanel): void {
    this.allegatiService.getOCR(allegato.streamId).subscribe(ocr => {
      this.testoOCROriginale = ocr;
      allegato.ocrTesto = ocr;
      this.selectedAllegato = allegato;
      overlaypanel.toggle(event);
    });
  }

  public clearPanel(): void {
    this.selectedAllegato = null;
    this.riproduciTutto = true;
    this.reproErrorMessage = null;
  }

  public clearOCRPanel(): void {
    this.selectedAllegato = null;
  }

  public validateInput(event: { value: string }): void {
    this.reproErrorMessage = '';
    const pageOrInterval = /^(\d+?)(?:-(\d+?))?$/;
    if (!(pageOrInterval.exec(event.value))) {
      this.reproErrorMessage = 'Inserire un numero di pagina o un intervallo nel formato valido es. "1", "2-12"';
      this.reproPages?.pop(); // remove last entry from values
    }
  }

  public changeReproClick(e: MouseEvent, allegato: DocumentoAllegato, opAllegato: OverlayPanel): void {
    e.stopPropagation();
    if (!this.selectedAllegato) return;
    this.selectedAllegato.riproduzione = this.getObjectFromRiproduzioneObject(this.reproPages, this.selectedAllegato.riproduzione.originalStreamId);
    this.changeRiproduzione.emit(this.selectedAllegato);
    opAllegato.hide();
    this.clearPanel();
  }

  public changeOCRClick(e: MouseEvent, allegato: DocumentoAllegato, opOCR: OverlayPanel): void {
    e.stopPropagation();
    if (allegato.ocrTesto !== this.testoOCROriginale) {
      this.modifyOCR.emit(allegato);
    }

    opOCR.hide();
    this.selectedAllegato = null;
  }

  public undoClick(e: MouseEvent, opAllegato: OverlayPanel): void {
    e.stopPropagation();
    opAllegato.hide();
    this.clearPanel();
  }

  public undoOCRClick(e: MouseEvent, opAllegato: OverlayPanel): void {
    e.stopPropagation();
    opAllegato.hide();
    this.selectedAllegato = null;
  }

  public onFileSelect(_: FileSelectEvent): void {
    this.syncPendingFiles();
  }

  public onFileRemove(event: FileRemoveEvent): void {
    if (event.file === this.lastQueuedScannedFile) {
      this.lastQueuedScannedFile = null;
      this.scanCancelled.emit();
    }
    // PrimeNG emette onRemove prima di eliminare il file dal proprio array.
    this.pendingFiles.set((this.fileupload()?.files ?? []).filter(file => file !== event.file));
  }

  public onFileClear(): void {
    if (this.lastQueuedScannedFile) {
      this.lastQueuedScannedFile = null;
      this.scanCancelled.emit();
    }
    this.syncPendingFiles();
  }

  public onUpload(e: FileUploadEvent): void {
    const uploadedFiles = e.files?.length ?? 1;
    const remaining = Math.max(0, this.pendingUploadCount() - uploadedFiles);

    if (remaining <= 0) {
      this.uploadCompleted.emit();
      this.closeAddAllegatiDialog();
    } else {
      this.pendingUploadCount.set(remaining);
    }
  }

  public onUploadError(e: FileUploadErrorEvent): void {
    this.uploadingInProgress.set(false);
    this.pendingUploadCount.set(0);
    this.syncPendingFiles();
    const errorMessages: string[] = [];
    const httpError = e.error as unknown as HttpErrorResponse;

    if (httpError?.status === 409) {
      errorMessages.push(e.error?.error);
    } else if (e.files.length > 1) {
      errorMessages.push(`Si è verificato un errore nel caricamento di uno dei file`);
    }
    else {
      errorMessages.push(`Si è verificato un errore nel caricamento del file ${e.files[0].name}`);
    }
    this.uploadError.emit(errorMessages.join('\n'));
  }

  public confirmUpload(): void {
    this.ensureScannedFileQueued();
    const filesCount = this.pendingFiles().length;
    this.uploadDescriptionTouched.set(true);
    if (!filesCount || this.uploadDescriptionValue().length === 0) {
      return;
    }

    this.pendingUploadCount.set(filesCount);
    this.uploadingInProgress.set(true);
    this.fileupload()?.upload();
  }

  public onUploadDescriptionChange(value: string): void {
    this.uploadDescription.set(value);
  }

  public markUploadDescriptionTouched(): void {
    this.uploadDescriptionTouched.set(true);
  }

  public onBeforeUpload(event: any): void {
    this.uploadDescriptionTouched.set(true);
    event.formData?.set('descrizione', this.uploadDescriptionValue());
    event.formData?.set('tipoAllegato', this.tipoAllegato().toString());
    const scanRequestId = this.pendingScanRequestId();
    if (scanRequestId) {
      event.formData?.set('pendingScanRequestId', scanRequestId);
    }
  }

  public getFileIconClass(allegato: DocumentoAllegato): string {
    const fileName = allegato?.isP7m && allegato.extractedFileName ? allegato.extractedFileName : allegato?.fileName;
    if (!fileName) { return ''; }
    const fileNameParts = fileName.split('.');
    const ext = fileNameParts[fileNameParts.length - 1].toLowerCase();

    switch (ext) {
      case 'pdf':
        return 'fas fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fas fa-file-word';
      case 'xls':
      case 'xlsx':
        return 'fas fa-file-excel';
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'tiff':
      case 'tif':
        return 'fas fa-file-image';
      default:
        return 'fas fa-file-alt';
    }
  }

  public hasOCR(allegato: DocumentoAllegato): boolean {
    return (typeof allegato.ocrTesto) !== 'undefined' && !!allegato.ocrTesto;
  }

  public isPDF(allegato: DocumentoAllegato): boolean {
    if (allegato.isP7m && allegato.extractedIsPdf) {
      return true;
    }

    return allegato.fileName.toLowerCase().endsWith('.pdf');
  }

  public loadAllegato(allegato: DocumentoAllegato): void {
    if (!allegato.fromSharedFolder) {
      this.downloadFile.emit(allegato);
    } else {
      this.allegatoToPreview.emit(allegato.fileName);
    }
  }

  public toggleAllegatiCondivisi(): void {
    this.mostraCondivisi.set(!this.mostraCondivisi());
    this.showAllegatiCondivisi.emit();
  }

  public showUploadAllegatoCondiviso(file: DocumentoAllegato): void {
    if (!this.updatingAllegati())
      this.allegatoToUpload.emit(file);
  }

  public openAddAllegatiDialog(): void {
    if (this.isLocked()) {
      return;
    }
    this.resetUploadState();
    this.showAddAllegatiDialog.set(true);
  }

  public closeAddAllegatiDialog(): void {
    this.resetUploadState();
    this.showAddAllegatiDialog.set(false);
    this.scanCancelled.emit();
  }

  public downloadOriginalSigned(event: MouseEvent, allegato: DocumentoAllegato): void {
    event.stopPropagation();
    this.allegatiService.downloadAllegatoOriginale(allegato);
  }

  private getRiproduciTuttoFromDocumento(repro: RiproduzioneJson): boolean {
    return repro.allPages;
  }

  private reproPagesFromDocumento(repro: RiproduzioneJson): string[] | null {
    return this.getArrayOfIntervalFromObject(repro.pageRange);
  }

  private getArrayOfIntervalFromObject(array: Intervallo[] | null | undefined): string[] | null {
    if (array) {
      return array.map(v => (v.from !== v.to) ? v.from.toString() + '-' + v.to.toString() : v.from.toString());
    } else {
      return null;
    }
  }

  private getObjectFromRiproduzioneObject(reproPages: string[] | null, originalStreamId: string): RiproduzioneJson {
    const a = this.getArrayofIntervallo(reproPages);
    return {
      allPages: this.riproduciTutto,
      originalStreamId,
      pageRange: a
    };
  }

  private getArrayofIntervallo(reproPages: string[] | null): Intervallo[] {
    const pages: Intervallo[] = [];
    reproPages?.forEach(element => {
      const intervallo = element.split('-');
      if (intervallo.length > 1) {
        // const i: Intervallo = {from: +intervallo[0], to: +intervallo[1]};
        pages.push({ from: +intervallo[0], to: +intervallo[1] });
      } else {
        pages.push({ from: +intervallo[0], to: +intervallo[0] });
      }
    });
    return pages;
  }

  private syncPendingFiles(): void {
    const files = this.fileupload()?.files ?? [];
    this.pendingFiles.set([...files]);
  }

  private queueScannedFile(scannedFile: File, uploader: FileUpload): void {
    uploader.files = [scannedFile];
    uploader.cd.markForCheck();
    this.pendingFiles.set([scannedFile]);
    this.lastQueuedScannedFile = scannedFile;
  }

  private ensureScannedFileQueued(): void {
    const scannedFile = this.scannedFile();
    if (!scannedFile || this.pendingFiles().includes(scannedFile)) {
      return;
    }

    const uploader = this.fileupload();
    if (uploader) {
      this.queueScannedFile(scannedFile, uploader);
    }
  }

  private resetUploadState(): void {
    this.fileupload()?.clear();
    this.pendingFiles.set([]);
    this.pendingUploadCount.set(0);
    this.uploadingInProgress.set(false);
    this.uploadDescription.set('');
    this.uploadDescriptionTouched.set(false);
  }
}
